import { Box } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Api } from '../../api';
import AppointmentCard from './AppointmentCard';
import AvailableSlot from './AvailableSlot';
import { Model } from '../../models';

interface DayColumnProps {
  day: { name: string; key: string; date: string };
  dayIndex: number;
  timezone: string;
  onAppointmentClick: (
    event: React.MouseEvent<HTMLElement>,
    appointment: Api.Appointment.V_ClinicianSub,
  ) => void;
  onAvailableSlotClick: (
    event: React.MouseEvent<HTMLElement>,
    timeSlot: string,
    date: string,
  ) => void;
  availableHours: { [date: string]: { start: string; end: string }[] };
  appointments: Api.Appointment.V_ClinicianSub[];
  days: Array<{ name: string; key: string; date: string }>;
  hours: string[];
  bufferMinutes: number;
}

export default function DayColumn({
  day,
  dayIndex,
  timezone,
  onAppointmentClick,
  onAvailableSlotClick,
  availableHours,
  appointments,
  days,
  hours,
  bufferMinutes,
}: DayColumnProps) {
  const isToday = day.date === format(new Date(), 'yyyy-MM-dd');

  const getDayAvailableBlocks = (date: string) => {
    const slots = availableHours[date] || [];
    return Model.Appointment.subtractAppointmentsFromWindows(
      slots,
      appointments,
      date,
      timezone,
      bufferMinutes,
    );
  };

  const getCellAppointments = (date: string, hour: string) => {
    const cellStartMinutes = Model.Appointment.timeToMinutes(hour);
    const cellEndMinutes = cellStartMinutes + 60;

    return appointments.filter((apt) => {
      const aptDateInTz = formatInTimeZone(
        parseISO(apt.startAt),
        timezone,
        'yyyy-MM-dd',
      );
      if (aptDateInTz !== date) return false;
      const aptTimeInTz = formatInTimeZone(
        parseISO(apt.startAt),
        timezone,
        'HH:mm',
      );
      const aptStartMinutes = Model.Appointment.timeToMinutes(aptTimeInTz);
      return (
        aptStartMinutes >= cellStartMinutes && aptStartMinutes < cellEndMinutes
      );
    });
  };

  const getAppointmentPosition = (
    appointment: Api.Appointment.V_ClinicianSub,
    hour: string,
  ) => {
    const cellStartMinutes = Model.Appointment.timeToMinutes(hour);
    const aptTimeInTz = formatInTimeZone(
      parseISO(appointment.startAt),
      timezone,
      'HH:mm',
    );
    const aptStartMinutes = Model.Appointment.timeToMinutes(aptTimeInTz);
    const aptEndMinutes = aptStartMinutes + appointment.duration;

    const top = ((aptStartMinutes - cellStartMinutes) / 60) * 60 + 4;
    const height = ((aptEndMinutes - aptStartMinutes) / 60) * 60 - 8;

    return { top, height };
  };

  const availableBlocks = getDayAvailableBlocks(day.date);

  const formatTimeSlot = (startMinutes: number, endMinutes: number) => {
    const formatMinutes = (minutes: number) => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins
        .toString()
        .padStart(2, '0')}`;
    };
    return `${formatMinutes(startMinutes)} - ${formatMinutes(endMinutes)}`;
  };

  const formatDate = (dayKey: string) => {
    const dayData = days.find((d) => d.key === dayKey);
    if (!dayData) return 'Unknown date';
    return dayData.date;
  };

  return (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        borderRight: dayIndex < days.length - 1 ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      {availableBlocks.map((block, blockIndex) => {
        const dayStartHour = parseInt(hours[0].split(':')[0]);
        const relativeStartMinutes = block.startMinutes - dayStartHour * 60;
        const top = (relativeStartMinutes / 60) * 60 + 4;
        const height = ((block.endMinutes - block.startMinutes) / 60) * 60 - 8;

        return (
          <AvailableSlot
            key={`available-${blockIndex}`}
            style={{ top: `${top}px`, height: `${height}px` }}
            onClick={(event) =>
              onAvailableSlotClick(
                event,
                formatTimeSlot(block.startMinutes, block.endMinutes),
                formatDate(day.key),
              )
            }
          />
        );
      })}

      {hours.map((hour, hourIndex) => {
        const cellAppointments = getCellAppointments(day.date, hour);

        return (
          <Box
            key={hour}
            sx={{
              position: 'relative',
              height: '60px',
              backgroundColor: isToday ? 'background.level1' : 'transparent',
              borderBottom: hourIndex < hours.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {cellAppointments.map((appointment, aptIndex) => {
              const position = getAppointmentPosition(appointment, hour);

              return (
                <AppointmentCard
                  key={`apt-${aptIndex}`}
                  appointment={appointment}
                  onClick={(event) => onAppointmentClick(event, appointment)}
                  timezone={timezone}
                  style={{
                    top: `${position.top}px`,
                    height: `${position.height}px`,
                    zIndex: 10,
                  }}
                />
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
